import axiosInstance from '../../../config/axios';

export interface ContactsCounts {
  CUSTOMER: number;
  PARTNER: number;
  SUPPLIER: number;
  total: number;
}

export interface StageCounts {
  LEAD: number;
  PROSPECT: number;
  ACTIVE: number;
  INACTIVE: number;
}

export interface NewCustomerByMonth {
  month: string;
  count: number;
}

export interface PaymentBehaviorSummary {
  good: number;
  ok: number;
  late: number;
  no_invoices: number;
}

export interface UntouchedCustomer {
  id: number;
  name: string;
  city?: string;
  created_at: string;
  has_offers: boolean;
  has_projects: boolean;
}

export interface ContactsSummary {
  counts: ContactsCounts;
  stages: StageCounts;
  customers: {
    new_this_month: number;
    new_last_month: number;
    new_by_month: NewCustomerByMonth[];
  };
  payment_behavior: PaymentBehaviorSummary;
  untouched_customers: {
    count: number;
    items: UntouchedCustomer[];
  };
}

function parseNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseInt(value, 10) || 0;
  return 0;
}

export default class BusinessPartnerSummaryService {
  static async getSummary(): Promise<ContactsSummary> {
    const response = await axiosInstance.get('/api/admin/business-partners/summary/');
    const data = response.data;

    const defaultSummary: ContactsSummary = {
      counts: { CUSTOMER: 0, PARTNER: 0, SUPPLIER: 0, total: 0 },
      stages: { LEAD: 0, PROSPECT: 0, ACTIVE: 0, INACTIVE: 0 },
      customers: { new_this_month: 0, new_last_month: 0, new_by_month: [] },
      payment_behavior: { good: 0, ok: 0, late: 0, no_invoices: 0 },
      untouched_customers: { count: 0, items: [] },
    };

    if (!data) return defaultSummary;

    const counts: ContactsCounts = {
      CUSTOMER: parseNumber(data.counts?.CUSTOMER),
      PARTNER: parseNumber(data.counts?.PARTNER),
      SUPPLIER: parseNumber(data.counts?.SUPPLIER),
      total: parseNumber(data.counts?.total),
    };

    const stages: StageCounts = {
      LEAD: parseNumber(data.stages?.LEAD),
      PROSPECT: parseNumber(data.stages?.PROSPECT),
      ACTIVE: parseNumber(data.stages?.ACTIVE),
      INACTIVE: parseNumber(data.stages?.INACTIVE),
    };

    const newByMonth: NewCustomerByMonth[] = (data.customers?.new_by_month ?? []).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (m: any) => ({
        month: m.month ?? '',
        count: parseNumber(m.count),
      })
    );

    const customers = {
      new_this_month: parseNumber(data.customers?.new_this_month),
      new_last_month: parseNumber(data.customers?.new_last_month),
      new_by_month: newByMonth,
    };

    const payment_behavior: PaymentBehaviorSummary = {
      good: parseNumber(data.payment_behavior?.good),
      ok: parseNumber(data.payment_behavior?.ok),
      late: parseNumber(data.payment_behavior?.late),
      no_invoices: parseNumber(data.payment_behavior?.no_invoices),
    };

    const untouchedItems: UntouchedCustomer[] = (data.untouched_customers?.items ?? []).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: any) => ({
        id: parseNumber(item.id),
        name: item.name ?? '',
        city: item.city ?? undefined,
        created_at: item.created_at ?? '',
        has_offers: Boolean(item.has_offers),
        has_projects: Boolean(item.has_projects),
      })
    );

    const untouched_customers = {
      count: parseNumber(data.untouched_customers?.count),
      items: untouchedItems,
    };

    return {
      counts,
      stages,
      customers,
      payment_behavior,
      untouched_customers,
    };
  }

  static formatMonthLabel(month: string): string {
    if (!month) return '';
    const [year, m] = month.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(m, 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return month;
    return `${monthNames[monthIndex]} ${year.slice(2)}`;
  }
}
